import { useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { emailApi } from '../../api/email'
import type { EmailMessage } from '../../api/email'
import { emailAccountsApi } from '../../api/settings'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useAuthStore } from '../../store/auth'
import { FolderColumn } from './FolderColumn'
import { MessageList } from './MessageList'
import { ReadingPane } from './ReadingPane'
import { ComposePane } from './ComposePane'

type Folder = 'inbox' | 'sent' | 'drafts' | 'scheduled' | 'trash'

export function MailPage() {
  usePageTitle('Mail')
  const { companyId } = useParams<{ companyId: string }>()
  const isSalesUser = useAuthStore(s => s.user?.role === 'sales_user')
  const [folder, setFolder] = useState<Folder>('inbox')
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const [selectedMsg, setSelectedMsg] = useState<EmailMessage | null>(null)
  const [composing, setComposing] = useState(false)

  // Sales users don't have access to email-accounts endpoint (admin-only)
  // and don't need the account switcher since email addresses are hidden from them.
  const { data: accountsData } = useQuery({
    queryKey: ['email-accounts', companyId],
    queryFn: () => emailAccountsApi.list(companyId!).then(r => r.data),
    enabled: !!companyId && !isSalesUser,
    staleTime: 60_000,
  })
  const accounts = (accountsData as any[] | undefined) ?? []

  const { data: inboxData, isLoading } = useQuery({
    queryKey: ['inbox', companyId, folder, selectedAccount, search],
    queryFn: () => emailApi.inbox(companyId!, { folder, accountId: selectedAccount, search, limit: 50 }).then(r => r.data),
    enabled: !!companyId,
    refetchInterval: 60_000,
  })

  const { data: scheduledData } = useQuery({
    queryKey: ['inbox', companyId, 'scheduled'],
    queryFn: () => emailApi.inbox(companyId!, { folder: 'scheduled', limit: 50 }).then(r => r.data),
    enabled: !!companyId,
    refetchInterval: 60_000,
  })

  const handleCompose = useCallback(() => {
    setComposing(true)
    setSelectedMsg(null)
  }, [])

  const messages = inboxData?.data ?? []
  const inboxCount = folder === 'inbox' ? (inboxData?.meta.total ?? 0) : 0
  const scheduledCount = scheduledData?.meta.total ?? 0

  return (
    <div className="flex h-full overflow-hidden">
      <FolderColumn
        folder={folder}
        onFolder={f => { setFolder(f); setSelectedMsg(null); setComposing(false) }}
        onCompose={handleCompose}
        accounts={accounts}
        selectedAccount={selectedAccount}
        onAccount={setSelectedAccount}
        inboxCount={inboxCount}
        scheduledCount={scheduledCount}
      />
      <MessageList
        messages={messages}
        selectedId={selectedMsg?.id ?? null}
        onSelect={msg => { setSelectedMsg(msg); setComposing(false) }}
        onSearch={setSearch}
        isLoading={isLoading}
      />
      {composing ? (
        <ComposePane
          companyId={companyId!}
          onClose={() => setComposing(false)}
        />
      ) : selectedMsg?.status === 'draft' && !selectedMsg.threadId ? (
        // New-message draft (no thread) → open in compose editor
        <ComposePane
          companyId={companyId!}
          draftMessage={selectedMsg}
          onClose={() => setSelectedMsg(null)}
        />
      ) : (
        <ReadingPane message={selectedMsg} />
      )}
    </div>
  )
}
